'use client';

import { memo, useCallback, useMemo } from 'react';

import { SegmentedControl, type SegmentedControlOption } from '@air/component-segmented-control';
import { InfoIcon } from '@air/design-icons';
import { Tooltip } from '@air/primitive-tooltip';

export type VersionReference = 'static' | 'dynamic';

export const isVersionReference = (value: string): value is VersionReference =>
  value === 'static' || value === 'dynamic';

const STATIC_OPTION: SegmentedControlOption = {
  id: 'static',
  label: 'Static',
  tooltip: 'URL references this specific version',
  value: 'static',
};

export type VersionReferenceRowProps = {
  versionReference: VersionReference;
  onVersionReferenceChange: (value: VersionReference) => void;
  /** Whether the Dynamic option is clickable */
  isDynamicEnabled?: boolean;
};

export const VersionReferenceRow = memo(
  ({ versionReference, onVersionReferenceChange, isDynamicEnabled = false }: VersionReferenceRowProps) => {
    const versionReferenceOptions = useMemo<SegmentedControlOption[]>(
      () => [
        STATIC_OPTION,
        {
          disabled: !isDynamicEnabled,
          id: 'dynamic',
          label: 'Dynamic',
          tooltip: isDynamicEnabled
            ? 'URL always points to the current version'
            : 'Dynamic version reference is not yet available',
          value: 'dynamic',
        },
      ],
      [isDynamicEnabled],
    );

    const selectedOption = useMemo(
      () => versionReferenceOptions.find((opt) => opt.value === versionReference) ?? versionReferenceOptions[0],
      [versionReference, versionReferenceOptions],
    );

    const handleVersionReferenceSelect = useCallback(
      (option: SegmentedControlOption) => {
        if (isVersionReference(option.value)) {
          onVersionReferenceChange(option.value);
        }
      },
      [onVersionReferenceChange],
    );

    return (
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-12 text-neutral-11">
          Asset version reference
          <Tooltip
            label={
              <ul className="list-disc pl-3">
                <li>Static: URL references this specific version</li>
                <li>Dynamic: URL always points to the current version</li>
              </ul>
            }
          >
            <span>
              <InfoIcon className="text-neutral-9" size="sm" />
            </span>
          </Tooltip>
        </span>
        <SegmentedControl
          color="gray"
          includeTooltip
          onSelect={handleVersionReferenceSelect}
          options={versionReferenceOptions}
          selectedOption={selectedOption}
          size="small"
        />
      </div>
    );
  },
);

VersionReferenceRow.displayName = 'VersionReferenceRow';
